/**
 * お客様にお願いしていること (ご対応のお願い) の判定。注文一覧の各行・一覧上部のお知らせ・注文詳細
 * 上部の「ご対応のお願い」欄がすべてここを読む (場所ごとに判定すると食い違う)。
 *
 * 一覧でも出すので、判定に使うのは一覧を組み立てるときに手元にあるデータだけにする (Shopify の
 * 注文・ロジレスの注文・返品申請)。済んだかどうかを把握できないもの (キャンセル後の返金口座の
 * お知らせ) は入れない — 入れると、送っていただいた後もずっと「ご対応のお願い」に残る。
 */

import type { UnifiedOrderStatus } from "./orderStatus";
import type { ActiveReturnRequestDTO } from "./orderTypes";
import { computeMypageReturnActions } from "./returnRequestActions";

export type ActionItem =
  /** 支払い前・期限切れ。支払いのやり直しの URL が取れない注文もある (そのときはボタンを出さない) */
  | { kind: "payment"; collectionUrl: string | null }
  /** 注文内容の変更で足した差額が未払い */
  | { kind: "additional_payment"; amountJpy: number; collectionUrl: string }
  /** ロジレスが住所不備で止めている */
  | { kind: "address_fix" }
  /** 返品申請。操作は返品のページ (CS が承認するまでは無いので null) */
  | { kind: "return_shipment"; customerPageUrl: string | null }
  | { kind: "return_bank_account"; customerPageUrl: string | null }
  | { kind: "return_handling_fee"; customerPageUrl: string | null };

export type ActionItemKind = ActionItem["kind"];

/** 一覧の各行・「ご対応のお願い」欄の見出しに使う短い名前 */
export const ACTION_ITEM_LABEL: Record<ActionItemKind, string> = {
  payment: "お支払い",
  additional_payment: "差額のお支払い",
  address_fix: "お届け先のご修正",
  return_shipment: "商品のご返送",
  return_bank_account: "ご返金先の口座のご入力",
  return_handling_fee: "事務手数料のお支払い",
};

const PAYMENT_PENDING_STATUSES: ReadonlySet<UnifiedOrderStatus> = new Set([
  "PENDING_PAYMENT",
  "EXPIRED_PAYMENT",
]);

const CLOSED_STATUSES: ReadonlySet<UnifiedOrderStatus> = new Set(["CANCELLED", "REFUNDED"]);

export type ActionItemInput = {
  status: UnifiedOrderStatus;
  /** Shopify `displayFinancialStatus` */
  financialStatus: string | null;
  /** Shopify `totalOutstandingSet` (円)。差額の未払いの判定に使う */
  outstandingJpy: number;
  /** Shopify `paymentCollectionDetails.additionalPaymentCollectionUrl` */
  paymentCollectionUrl: string | null;
  hasAddressDeficiency: boolean;
  activeReturnRequest: ActiveReturnRequestDTO | null;
};

/**
 * お客様にお願いしていることを、お願いしたい順に返す。
 *
 * 差額の未払いは注文のステータスにしない (出荷の可否の判定や、支払い前の注文に付く 7 日の自動
 * キャンセルの注意書きと混ぜない)。一部支払い済み (`PARTIALLY_PAID`) で未払い額が残り、支払いの
 * リンクが取れるときだけ出す。
 */
export const deriveActionItems = (input: ActionItemInput): ActionItem[] => {
  const items: ActionItem[] = [];
  if (CLOSED_STATUSES.has(input.status)) return items;

  if (PAYMENT_PENDING_STATUSES.has(input.status)) {
    items.push({ kind: "payment", collectionUrl: input.paymentCollectionUrl });
  } else if (
    input.financialStatus === "PARTIALLY_PAID" &&
    input.outstandingJpy > 0 &&
    input.paymentCollectionUrl
  ) {
    items.push({
      kind: "additional_payment",
      amountJpy: input.outstandingJpy,
      collectionUrl: input.paymentCollectionUrl,
    });
  }

  if (input.hasAddressDeficiency) items.push({ kind: "address_fix" });

  if (input.activeReturnRequest) {
    const customerPageUrl = input.activeReturnRequest.customerPageUrl;
    for (const action of computeMypageReturnActions(input.activeReturnRequest)) {
      if (action.kind === "needs_return_shipment") {
        items.push({ kind: "return_shipment", customerPageUrl });
      } else if (action.kind === "needs_bank_account") {
        items.push({ kind: "return_bank_account", customerPageUrl });
      } else if (action.kind === "needs_handling_fee_payment") {
        items.push({ kind: "return_handling_fee", customerPageUrl });
      }
      // handling_fee_pending (入金の反映待ち) はお客様にお願いすることが無いので出さない
    }
  }

  return items;
};
